import { useCallback, useRef } from "react";

import { useCachedResource } from "src-next/shared/cache/useCachedResource";
import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "src-next/modules/crm-clinical/messageTemplates/constants";

import { automationService } from "../services/automationService";
import type { AutomationContext, AutomationId, AutomationItem } from "../types";

interface UseAutomationServiceOptions {
  /**
   * Só busca depois da primeira abertura da seção.
   * O refresh de troca de canal pode forçar a rede mesmo com isto falso.
   */
  enabled?: boolean;
}

interface UseAutomationServiceResult {
  automations: AutomationItem[];
  isLoading: boolean;
  search: (params: { term?: string }) => Promise<void>;
  refresh: () => Promise<void>;
  disableAutomation: (params: { automationId: AutomationId }) => Promise<void>;
}

const EMPTY_AUTOMATIONS: AutomationItem[] = [];

export function useAutomationService(
  context: AutomationContext,
  options: UseAutomationServiceOptions = {},
): UseAutomationServiceResult {
  const enabled = options.enabled ?? true;
  const searchTermRef = useRef("");

  const organizationId = context.organizationId?.trim() || "";
  const practitionerId = context.practitionerId?.trim() || "";
  /**
   * Mesmo fallback do service. A chave leva o WABA efetivo para a lista
   * de uma conta não ser reaproveitada na outra. Id vazio não entra:
   * sem organização não há busca.
   */
  const resolvedWabaId =
    context.whatsappBusinessAccountId?.trim() ||
    DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const cacheKey = organizationId
    ? `crm:automations:${organizationId}:${practitionerId}:${resolvedWabaId}`
    : null;

  const stableContextRef = useRef<AutomationContext>({
    organizationId,
    practitionerId: practitionerId || undefined,
    whatsappBusinessAccountId: context.whatsappBusinessAccountId,
  });
  stableContextRef.current = {
    organizationId,
    practitionerId: practitionerId || undefined,
    whatsappBusinessAccountId: context.whatsappBusinessAccountId,
  };

  const { data, isLoading, refresh: refreshCache } = useCachedResource<
    AutomationItem[]
  >({
    cacheKey,
    enabled: enabled && Boolean(cacheKey),
    fetcher: () =>
      automationService.listByContext({
        context: stableContextRef.current,
        filters: { search: searchTermRef.current },
      }),
  });

  const refresh = useCallback(async () => {
    await refreshCache();
  }, [refreshCache]);

  const search = useCallback(
    async ({ term = "" }: { term?: string }) => {
      searchTermRef.current = term;
      await refreshCache();
    },
    [refreshCache],
  );

  const disableAutomation = useCallback(
    async ({ automationId }: { automationId: AutomationId }) => {
      await automationService.removeConfigByAutomationId({
        context: stableContextRef.current,
        automationId,
      });
      await refreshCache();
    },
    [refreshCache],
  );

  return {
    automations: data ?? EMPTY_AUTOMATIONS,
    isLoading,
    search,
    refresh,
    disableAutomation,
  };
}
